import React, { useState, useEffect } from 'react';
import { supabase, supabase_get_authorized_client_ids } from '../services/supabaseService';
import { formatClientFourDigitId } from '../utils/clientDisplay';
import { Building2, Users, Search, AlertCircle, RefreshCw, LogIn, ExternalLink, Briefcase, Plus, Edit, Trash2, ChevronRight, Mail, Phone, Loader2, ChevronUp, ChevronDown, Filter, X } from 'lucide-react';
import { User, UserRole } from '../types';
import CRMDashboard from './CRMDashboard';
import { searchTextIncludes, searchTextIncludesAny } from '../utils/searchText';

interface CRMManagerProps {
    user: User;
    onNavigateVacancy?: (vacancyId: string) => void;
    onNavigateCandidate?: (candidateId: string) => void;
    /** Called after CRM sets session filters for shortlist drill-down. */
    onNavigateToShortlist?: () => void;
    /** Called after CRM sets session filter for vacancy list (company name). */
    onNavigateToVacancies?: () => void;
    initialEntityId?: string | null;
    onLoginAs?: (clientId: string, clientName: string, portalTestEmail?: string) => void;
}

type CrmCompanyRowStats = { contacts: number; shortlist: number; vacancies: number };

function isPipelineShortlistStatus(status: string | null | undefined): boolean {
    const s = String(status || '').toLowerCase();
    return s.includes('shortlist');
}

const CRM_SHORTLIST_ORG_SESSION_KEY = 'wt:crm_shortlist_org_client_id';
const CRM_SHORTLIST_STATUS_FOCUS_KEY = 'wt:crm_shortlist_status_focus';
const VACANCIES_CRM_CLIENT_NAME_SESSION_KEY = 'wt:vacancies_filter_crm_client_name';

const CRMManager: React.FC<CRMManagerProps> = ({
    user,
    onNavigateVacancy,
    onNavigateCandidate,
    onNavigateToShortlist,
    onNavigateToVacancies,
    initialEntityId,
    onLoginAs,
}) => {
    const [activeTab, setActiveTab] = useState<'companies' | 'contacts'>('companies');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    
    const [companies, setCompanies] = useState<any[]>([]);
    const [contacts, setContacts] = useState<any[]>([]);
    const [clientPortalInvites, setClientPortalInvites] = useState<string[]>([]);
    const [crmCompanyStats, setCrmCompanyStats] = useState<Record<string, CrmCompanyRowStats>>({});
    /** When set, Contactpersonen tab only shows contacts linked to this company. */
    const [contactsLinkedCompanyId, setContactsLinkedCompanyId] = useState<string | null>(null);

    // Filter & Sort State
    const [filterType, setFilterType] = useState<string>('all'); // all, klant, eindklant, leverancier
    const [filterSource, setFilterSource] = useState<string>('all'); // all, bullhorn, local
    const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' } | null>(null);

    // Editor Modal State
    const [showDashboard, setShowDashboard] = useState(false);
    const [editingEntity, setEditingEntity] = useState<any>(null);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            let clientQuery = supabase
                .from('clients')
                .select('*')
                .eq('client_type', 'Direct')
                .order('name');
                
            if (user.role === UserRole.CLIENT && user.clientId) {
                clientQuery = clientQuery.eq('parent_client_id', user.clientId);
            }
                
            const { data: clientData, error: clientErr } = await clientQuery;
            if (clientErr) throw clientErr;
            setCompanies(clientData || []);

            let contactQuery = supabase
                .from('client_contacts')
                .select(`
                    id, first_name, last_name, email, phone, job_title, status, created_at, client_id, linked_client_id, bullhorn_id,
                    linked_org:clients!client_contacts_linked_client_id_fkey ( id, name, parent_client_id, active_clientportal )
                `)
                .order('first_name');
                
            if (user.role === UserRole.CLIENT && user.clientId) {
                const allowed = await supabase_get_authorized_client_ids(user.clientId);
                if (allowed?.length) contactQuery = contactQuery.in('linked_client_id', allowed);
                else contactQuery = contactQuery.eq('id', '00000000-0000-0000-0000-000000000000');
            }

            const { data: contactData, error: contactErr } = await contactQuery;
            if (contactErr) throw contactErr;
            
            setContacts(contactData || []);

            const companyRows = clientData || [];
            const stats: Record<string, CrmCompanyRowStats> = {};
            companyRows.forEach((c: any) => {
                stats[c.id] = { contacts: 0, shortlist: 0, vacancies: 0 };
            });
            (contactData || []).forEach((ct: any) => {
                const lid = ct.linked_client_id;
                if (lid && stats[lid]) stats[lid].contacts += 1;
            });

            let vacQuery = supabase
                .from('vacancies')
                .select('id, client_id, eindklant_id, opdrachtgever_id, data')
                .order('created_at', { ascending: false })
                .limit(8000);
            const { data: vacRows, error: vacErr } = await vacQuery;
            if (!vacErr && vacRows?.length) {
                const companyIdSet = new Set(companyRows.map((c: any) => c.id));
                const vacToCompanies = new Map<string, Set<string>>();
                vacRows.forEach((v: any) => {
                    const d = v.data || {};
                    const touched = [
                        v.client_id,
                        v.eindklant_id,
                        v.opdrachtgever_id,
                        d.eindklant_id,
                        d.opdrachtgever_id,
                        d.client_id,
                    ]
                        .map((x: any) => String(x || '').trim())
                        .filter((id) => id && companyIdSet.has(id));
                    const uniq = [...new Set(touched)];
                    uniq.forEach((cid) => {
                        if (stats[cid]) stats[cid].vacancies += 1;
                    });
                    if (uniq.length) vacToCompanies.set(v.id, new Set(uniq));
                });
                const vacIds = [...new Set(vacRows.map((v: any) => v.id).filter(Boolean))];
                const APP_CHUNK = 120;
                const shortlistApps: { vacancy_id: string }[] = [];
                for (let i = 0; i < vacIds.length; i += APP_CHUNK) {
                    const chunk = vacIds.slice(i, i + APP_CHUNK);
                    const { data: appChunk } = await supabase
                        .from('applications')
                        .select('vacancy_id, status')
                        .in('vacancy_id', chunk);
                    (appChunk || []).forEach((a: any) => {
                        if (isPipelineShortlistStatus(a.status)) shortlistApps.push({ vacancy_id: a.vacancy_id });
                    });
                }
                shortlistApps.forEach((a) => {
                    const set = vacToCompanies.get(a.vacancy_id);
                    set?.forEach((cid) => {
                        if (stats[cid]) stats[cid].shortlist += 1;
                    });
                });
            }
            setCrmCompanyStats(stats);

            // Ophalen portaal invites
            let inviteQuery = supabase.from('client_portal_invites').select('email');
            if (user.role === UserRole.CLIENT && user.clientId) {
                // Not ideal depending on RLS, but for client admins they can see their own
            }
            const { data: invitesData } = await inviteQuery;
            if (invitesData) {
                setClientPortalInvites(invitesData.map(i => i.email?.toLowerCase()).filter(Boolean));
            }

        } catch (e: any) {
            console.error(e);
            setError(e.message || "Fout bij ophalen van relatie-data.");
        }
        setLoading(false);
    };

    useEffect(() => {
        loadData();
    }, []);

    // Automatically open the targeted entity when initialEntityId changes
    useEffect(() => {
        if (!initialEntityId) return;
        if (loading) return; // Wait for data to load
        
        let entity = companies.find(c => c.id === initialEntityId);
        if (entity) {
            setActiveTab('companies');
            setSearchQuery('');
            setEditingEntity(entity);
            setShowDashboard(true);
            return;
        }
        
        entity = contacts.find(c => c.id === initialEntityId);
        if (entity) {
            setActiveTab('contacts');
            setSearchQuery('');
            setEditingEntity(entity);
            setShowDashboard(true);
            return;
        }
    }, [initialEntityId, companies, contacts, loading]);

    useEffect(() => {
        if (editingEntity) {
            const title = activeTab === 'companies' ? editingEntity.name : `${editingEntity.first_name || ''} ${editingEntity.last_name || ''}`.trim();
            window.dispatchEvent(new CustomEvent('wt:taskbar-add', { 
                detail: { id: editingEntity.id, type: 'crm', title: title || 'Relatie Profiel' } 
            }));
        }
    }, [editingEntity, activeTab]);

    const handleAdd = () => {
        setEditingEntity(null);
        setShowDashboard(true);
    };

    const handleEdit = (entity: any) => {
        setEditingEntity(entity);
        setShowDashboard(true);
    };

    const handleDelete = async (id: string, type: 'companies' | 'contacts') => {
        if (!window.confirm('Weet je zeker dat je dit item wilt verwijderen?')) return;
        
        try {
            const table = type === 'companies' ? 'clients' : 'client_contacts';
            const { error: delErr } = await supabase.from(table).delete().eq('id', id);
            if (delErr) throw delErr;
            
            await loadData();
        } catch (e: any) {
            console.error(e);
            alert("Verwijderen mislukt: " + e.message);
        }
    };

    const handleSort = (key: string) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedData = (data: any[], type: 'companies'|'contacts') => {
        if (!sortConfig) return data;
        return [...data].sort((a, b) => {
            let aValue = '';
            let bValue = '';
            
            if (type === 'companies') {
                if (sortConfig.key === 'bullhorn_id') { aValue = a.bullhorn_id || ''; bValue = b.bullhorn_id || ''; }
                if (sortConfig.key === 'name') { aValue = a.name || ''; bValue = b.name || ''; }
                if (sortConfig.key === 'phone') { aValue = a.phone || ''; bValue = b.phone || ''; }
                if (sortConfig.key === 'street') { aValue = a.address_street || ''; bValue = b.address_street || ''; }
                if (sortConfig.key === 'email') { aValue = a.email || ''; bValue = b.email || ''; }
                if (sortConfig.key === 'id4') { aValue = formatClientFourDigitId(a); bValue = formatClientFourDigitId(b); }
                if (sortConfig.key === 'adres') {
                    aValue = `${a.address_zip || ''} ${a.address_city || ''}`.trim();
                    bValue = `${b.address_zip || ''} ${b.address_city || ''}`.trim();
                }
                if (sortConfig.key === 'source') { aValue = (a.bullhorn_id ? 'bullhorn' : 'local'); bValue = (b.bullhorn_id ? 'bullhorn' : 'local'); }
            } else {
                if (sortConfig.key === 'bullhorn_id') { aValue = a.bullhorn_id || ''; bValue = b.bullhorn_id || ''; }
                if (sortConfig.key === 'name') { aValue = `${a.first_name} ${a.last_name}`; bValue = `${b.first_name} ${b.last_name}`; }
                if (sortConfig.key === 'company') { aValue = a.linked_org?.name || ''; bValue = b.linked_org?.name || ''; }
                if (sortConfig.key === 'source') { aValue = (a.bullhorn_id ? 'bullhorn' : 'local'); bValue = (b.bullhorn_id ? 'bullhorn' : 'local'); }
            }
            
            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const filteredCompanies = getSortedData(companies.filter(c => {
        const matchesSearch =
            !searchQuery.trim() ||
            searchTextIncludesAny(
                searchQuery,
                c.name,
                c.address_city,
                c.address_street,
                c.address_zip,
                c.phone,
                c.email,
            ) ||
            formatClientFourDigitId(c).includes(searchQuery.trim());
        const matchesType = filterType === 'all' || 
            (filterType === 'klant' && c.is_opdrachtgever) || 
            (filterType === 'eindklant' && c.is_eindklant) || 
            (filterType === 'leverancier' && c.is_leverancier);
        const matchesSource = filterSource === 'all' || 
            (filterSource === 'bullhorn' && c.bullhorn_id) || 
            (filterSource === 'local' && !c.bullhorn_id);
        return matchesSearch && matchesType && matchesSource;
    }), 'companies');

    const filteredContacts = getSortedData(contacts.filter(c => {
        if (contactsLinkedCompanyId && c.linked_client_id !== contactsLinkedCompanyId) return false;
        const matchesSearch =
            searchTextIncludes(`${c.first_name} ${c.last_name}`, searchQuery) ||
            searchTextIncludes(c.linked_org?.name, searchQuery) ||
            searchTextIncludes(c.email, searchQuery);
        const matchesSource = filterSource === 'all' || 
            (filterSource === 'bullhorn' && c.bullhorn_id) || 
            (filterSource === 'local' && !c.bullhorn_id);
        return matchesSearch && matchesSource;
    }), 'contacts');

    const companyRowStats = (id: string) => crmCompanyStats[id] || { contacts: 0, shortlist: 0, vacancies: 0 };

    const openContactsForCompany = (e: React.MouseEvent, companyId: string) => {
        e.stopPropagation();
        setActiveTab('contacts');
        setContactsLinkedCompanyId(companyId);
        setSearchQuery('');
    };

    const openShortlistForCompany = (e: React.MouseEvent, companyId: string) => {
        e.stopPropagation();
        try {
            sessionStorage.setItem(CRM_SHORTLIST_ORG_SESSION_KEY, companyId);
            sessionStorage.setItem(CRM_SHORTLIST_STATUS_FOCUS_KEY, '1');
        } catch {
            /* ignore */
        }
        onNavigateToShortlist?.();
    };

    const openVacanciesForCompany = (e: React.MouseEvent, companyName: string) => {
        e.stopPropagation();
        try {
            const n = (companyName || '').trim();
            if (n) sessionStorage.setItem(VACANCIES_CRM_CLIENT_NAME_SESSION_KEY, n);
        } catch {
            /* ignore */
        }
        onNavigateToVacancies?.();
    };

    return (
        <div className="flex flex-col h-[calc(100vh-64px)] bg-white animate-fadeIn">
            {/* Header Area */}
            <div className="px-6 lg:px-10 pt-3 pb-1">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
                    <div>
                        <h1 className="text-xl font-black text-slate-900 tracking-tight leading-tight flex items-center gap-2">
                            <Briefcase className="text-[#e51b23] shrink-0" size={24} /> Relatiebeheer (CRM)
                        </h1>
                        <p className="text-slate-500 text-xs font-medium mt-1">
                            Beheer bedrijven en contactpersonen. Een nieuwe contactpersoon maak je via het bedrijfsprofiel (tab Contactpersonen).
                        </p>
                    </div>
                    <div className="flex items-center gap-3 w-full md:w-auto">
                        <button
                            onClick={loadData}
                            disabled={loading}
                            className="bg-white hover:bg-slate-50 border border-slate-200 text-slate-600 px-3 py-2.5 rounded-xl text-xs font-bold shadow-sm transition-all"
                            title="Vernieuwen"
                        >
                            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                        </button>
                        {activeTab === 'companies' && (
                            <button
                                onClick={handleAdd}
                                className="bg-[#e51b23] hover:bg-red-700 text-white px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-2 shadow-lg transition-all active:scale-95 whitespace-nowrap"
                            >
                                <Plus size={16} /> <span className="hidden sm:inline">Nieuw bedrijf</span>
                            </button>
                        )}
                    </div>
                </div>
                
                {/* Tab Switcher */}
                <div className="flex gap-6 border-b border-slate-200 mb-2">
                    <button 
                         onClick={() => {
                             setActiveTab('companies');
                             setContactsLinkedCompanyId(null);
                         }}
                         className={`pb-3 text-[11px] font-black uppercase tracking-widest border-b-[3px] transition-colors flex items-center gap-2 ${activeTab === 'companies' ? 'border-[#e51b23] text-[#e51b23]' : 'border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300'}`}
                    >
                         <Building2 size={14} /> Bedrijven ({companies.length})
                    </button>
                    <button 
                         onClick={() => setActiveTab('contacts')}
                         className={`pb-3 text-[11px] font-black uppercase tracking-widest border-b-[3px] transition-colors flex items-center gap-2 ${activeTab === 'contacts' ? 'border-[#e51b23] text-[#e51b23]' : 'border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300'}`}
                    >
                         <Users size={14} /> Contactpersonen ({contacts.length})
                    </button>
                </div>
            </div>

            {error && (
                <div className="mx-6 lg:mx-10 mb-2 p-3 bg-red-50 text-red-600 rounded-xl text-xs font-bold flex items-center gap-2 border border-red-100 animate-fadeIn">
                    <AlertCircle size={14} /> {error}
                </div>
            )}

            {/* Table Area */}
            <div className="flex-1 overflow-hidden relative w-full px-4 lg:px-6 pb-2 pt-2 animate-fadeIn flex flex-col">
                
                {/* Search & Filter Toolbar */}
                {!showDashboard && (
                    <div className="bg-white rounded-[1.5rem] border border-slate-200 shadow-sm p-4 mb-4 flex flex-col gap-3 shrink-0 w-full z-10 sticky top-0">
                        {activeTab === 'contacts' && contactsLinkedCompanyId && (
                            <div className="flex items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
                                <span className="text-[11px] font-bold text-amber-900">
                                    Alleen contactpersonen van:{' '}
                                    <strong>{companies.find((x) => x.id === contactsLinkedCompanyId)?.name || 'dit bedrijf'}</strong>
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setContactsLinkedCompanyId(null)}
                                    className="text-[10px] font-black uppercase tracking-widest text-amber-800 hover:text-amber-950 shrink-0"
                                >
                                    Filter wissen
                                </button>
                            </div>
                        )}
                        <div className="flex flex-col sm:flex-row gap-4 items-center w-full">
                        {/* Zoekbalk */}
                        <div className="relative w-full sm:w-80 group">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#e51b23] transition-colors" size={16} />
                            <input
                                type="text"
                                placeholder={`Zoek in ${activeTab === 'companies' ? 'bedrijven' : 'contactpersonen'}...`}
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-200 text-slate-900 rounded-xl pl-10 pr-10 py-2.5 text-sm font-medium outline-none transition-all focus:bg-white focus:ring-2 focus:ring-red-100 focus:border-[#e51b23]"
                            />
                            {searchQuery && (
                                <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                    <X size={14} />
                                </button>
                            )}
                        </div>

                        {/* Filters */}
                        <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0 scrollbar-hide">
                            <span className="text-slate-400 mr-1"><Filter size={16} /></span>
                            
                            {activeTab === 'companies' && (
                                <select 
                                    value={filterType} 
                                    onChange={(e) => setFilterType(e.target.value)}
                                    className="bg-white border border-slate-200 text-slate-600 rounded-lg px-3 py-2 text-xs font-bold outline-none cursor-pointer appearance-none pr-8 relative bg-no-repeat bg-[position:right_10px_center] bg-[length:12px] focus:ring-1 focus:ring-[#e51b23] focus:border-[#e51b23]"
                                    title="Filter op relatietype"
                                    style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")" }}
                                >
                                    <option value="all">Alle Types</option>
                                    <option value="klant">Klant</option>
                                    <option value="eindklant">Eindklant</option>
                                    <option value="leverancier">Leverancier</option>
                                </select>
                            )}

                            <select 
                                value={filterSource} 
                                onChange={(e) => setFilterSource(e.target.value)}
                                className="bg-white border border-slate-200 text-slate-600 rounded-lg px-3 py-2 text-xs font-bold outline-none cursor-pointer appearance-none pr-8 relative bg-no-repeat bg-[position:right_10px_center] bg-[length:12px] focus:ring-1 focus:ring-[#e51b23] focus:border-[#e51b23]"
                                title="Filter op bron"
                                style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")" }}
                            >
                                <option value="all">Alle Bronnen</option>
                                <option value="bullhorn">Bullhorn ERP</option>
                                <option value="local">Lokaal Froukje</option>
                            </select>
                        </div>
                        </div>
                    </div>
                )}

                <div className="flex flex-1 gap-6 overflow-hidden h-full">
                    {!showDashboard && (
                        <div className="flex flex-col gap-4 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] w-full">
                            <div className="flex-1 overflow-x-auto bg-white rounded-[1.5rem] shadow-sm border border-slate-200 flex flex-col relative w-full">
                                <div className="flex-1 overflow-y-auto custom-scrollbar bg-white">
                                <table className="w-full text-left border-collapse">
                                    <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
                                        <tr>
                                            {activeTab === 'companies' ? (
                                                <>
                                                    <th className="px-3 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors select-none group whitespace-nowrap" onClick={() => handleSort('id4')}>
                                                        <div className="flex items-center gap-1">ID {sortConfig?.key === 'id4' ? (sortConfig.direction === 'asc' ? <ChevronUp size={12} className="text-[#e51b23]" /> : <ChevronDown size={12} className="text-[#e51b23]" />) : <ChevronDown size={12} className="opacity-0 group-hover:opacity-50" />}</div>
                                                    </th>
                                                    <th className="px-3 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors select-none group" onClick={() => handleSort('name')}>
                                                        <div className="flex items-center gap-1">Naam {sortConfig?.key === 'name' ? (sortConfig.direction === 'asc' ? <ChevronUp size={12} className="text-[#e51b23]" /> : <ChevronDown size={12} className="text-[#e51b23]" />) : <ChevronDown size={12} className="opacity-0 group-hover:opacity-50" />}</div>
                                                    </th>
                                                    <th className="px-3 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors select-none group" onClick={() => handleSort('street')}>
                                                        <div className="flex items-center gap-1">Straat {sortConfig?.key === 'street' ? (sortConfig.direction === 'asc' ? <ChevronUp size={12} className="text-[#e51b23]" /> : <ChevronDown size={12} className="text-[#e51b23]" />) : <ChevronDown size={12} className="opacity-0 group-hover:opacity-50" />}</div>
                                                    </th>
                                                    <th className="px-3 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors select-none group whitespace-nowrap" onClick={() => handleSort('adres')}>
                                                        <div className="flex items-center gap-1">Adres {sortConfig?.key === 'adres' ? (sortConfig.direction === 'asc' ? <ChevronUp size={12} className="text-[#e51b23]" /> : <ChevronDown size={12} className="text-[#e51b23]" />) : <ChevronDown size={12} className="opacity-0 group-hover:opacity-50" />}</div>
                                                    </th>
                                                    <th className="px-3 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors select-none group whitespace-nowrap" onClick={() => handleSort('phone')}>
                                                        <div className="flex items-center gap-1">Telefoon {sortConfig?.key === 'phone' ? (sortConfig.direction === 'asc' ? <ChevronUp size={12} className="text-[#e51b23]" /> : <ChevronDown size={12} className="text-[#e51b23]" />) : <ChevronDown size={12} className="opacity-0 group-hover:opacity-50" />}</div>
                                                    </th>
                                                    <th className="px-3 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors select-none group" onClick={() => handleSort('email')}>
                                                        <div className="flex items-center gap-1">E-mail {sortConfig?.key === 'email' ? (sortConfig.direction === 'asc' ? <ChevronUp size={12} className="text-[#e51b23]" /> : <ChevronDown size={12} className="text-[#e51b23]" />) : <ChevronDown size={12} className="opacity-0 group-hover:opacity-50" />}</div>
                                                    </th>
                                                    <th className="px-2 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center whitespace-nowrap">Contacten</th>
                                                    <th className="px-2 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center whitespace-nowrap">Shortlist</th>
                                                    <th className="px-2 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center whitespace-nowrap">Vacatures</th>
                                                </>
                                            ) : (
                                                <>
                                                    <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors select-none group" onClick={() => handleSort('bullhorn_id')}>
                                                        <div className="flex items-center gap-1">Cont.Nr. {sortConfig?.key === 'bullhorn_id' ? (sortConfig.direction === 'asc' ? <ChevronUp size={12} className="text-[#e51b23]" /> : <ChevronDown size={12} className="text-[#e51b23]" />) : <ChevronDown size={12} className="opacity-0 group-hover:opacity-50" />}</div>
                                                    </th>
                                                    <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors select-none group" onClick={() => handleSort('name')}>
                                                        <div className="flex items-center gap-1">Contactpersoon {sortConfig?.key === 'name' ? (sortConfig.direction === 'asc' ? <ChevronUp size={12} className="text-[#e51b23]" /> : <ChevronDown size={12} className="text-[#e51b23]" />) : <ChevronDown size={12} className="opacity-0 group-hover:opacity-50" />}</div>
                                                    </th>
                                                    <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest hidden md:table-cell cursor-pointer hover:bg-slate-100 transition-colors select-none group" onClick={() => handleSort('company')}>
                                                        <div className="flex items-center gap-1">Bedrijf & Functie {sortConfig?.key === 'company' ? (sortConfig.direction === 'asc' ? <ChevronUp size={12} className="text-[#e51b23]" /> : <ChevronDown size={12} className="text-[#e51b23]" />) : <ChevronDown size={12} className="opacity-0 group-hover:opacity-50" />}</div>
                                                    </th>
                                                    <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center hidden md:table-cell cursor-pointer hover:bg-slate-100 transition-colors select-none group" onClick={() => handleSort('source')}>
                                                        <div className="flex items-center justify-center gap-1">Sync Bron {sortConfig?.key === 'source' ? (sortConfig.direction === 'asc' ? <ChevronUp size={12} className="text-[#e51b23]" /> : <ChevronDown size={12} className="text-[#e51b23]" />) : <ChevronDown size={12} className="opacity-0 group-hover:opacity-50" />}</div>
                                                    </th>
                                                    <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Acties</th>
                                                </>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {loading ? (
                                            <tr>
                                                <td colSpan={activeTab === 'companies' ? 9 : 5} className="py-20 text-center">
                                                    <Loader2 className="animate-spin text-slate-300 mx-auto mb-2" size={32} />
                                                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Data laden...</p>
                                                </td>
                                            </tr>
                                        ) : activeTab === 'companies' ? (
                                            filteredCompanies.length === 0 ? (
                                                <tr><td colSpan={9} className="py-20 text-center"><p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Geen bedrijven gevonden.</p></td></tr>
                                            ) : (
                                                filteredCompanies.map((c) => {
                                                    const st = companyRowStats(c.id);
                                                    return (
                                                    <tr key={c.id} onClick={() => handleEdit(c)} className="cursor-pointer transition-all hover:bg-slate-50 group border-l-4 border-l-transparent hover:border-l-red-300">
                                                        <td className="px-3 py-2.5 align-top whitespace-nowrap">
                                                            <span className="text-[11px] font-mono font-black text-slate-700 tabular-nums">{formatClientFourDigitId(c)}</span>
                                                        </td>
                                                        <td className="px-3 py-2.5 min-w-[140px] max-w-[220px]">
                                                            <div className="flex items-start gap-2 min-w-0">
                                                                <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold border overflow-hidden shrink-0 bg-slate-100 border-slate-200 text-slate-500">
                                                                    {c.logo_url ? <img src={c.logo_url} alt="" className="w-full h-full object-cover" /> : <Building2 size={12}/>}
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <h4 className="text-[11px] font-black leading-tight truncate text-slate-900 group-hover:text-[#e51b23] transition-colors">{c.name}</h4>
                                                                    <div className="flex flex-wrap gap-0.5 mt-0.5 items-center">
                                                                        {c.is_opdrachtgever && <span className="bg-emerald-50 text-emerald-700 px-1 py-0.5 rounded text-[7px] font-black uppercase tracking-tighter border border-emerald-100">Klant</span>}
                                                                        {c.is_eindklant && <span className="bg-purple-50 text-purple-700 px-1 py-0.5 rounded text-[7px] font-black uppercase tracking-tighter border border-purple-100">Eindklant</span>}
                                                                        {c.is_leverancier && <span className="bg-slate-50 text-slate-600 px-1 py-0.5 rounded text-[7px] font-black uppercase tracking-tighter border border-slate-200">Levr.</span>}
                                                                        {c.active_clientportal && (
                                                                            <span className="bg-blue-50 text-blue-700 px-1 py-0.5 rounded text-[7px] font-black uppercase tracking-tighter border border-blue-200 inline-flex items-center gap-0.5">
                                                                                <ExternalLink size={7} /> Portaal
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-2.5 text-[11px] text-slate-700 max-w-[160px]">
                                                            <span className="line-clamp-2 break-words">{c.address_street?.trim() || '—'}</span>
                                                        </td>
                                                        <td className="px-3 py-2.5 text-[11px] text-slate-600 whitespace-nowrap">
                                                            {[c.address_zip, c.address_city].filter(Boolean).join(' ') || '—'}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-[11px] text-slate-700 whitespace-nowrap">
                                                            {c.phone ? <span className="flex items-center gap-1"><Phone size={10} className="text-slate-400 shrink-0" />{c.phone}</span> : '—'}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-[11px] max-w-[180px]">
                                                            {c.email ? (
                                                                <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="text-blue-600 font-bold truncate block hover:underline">{c.email}</a>
                                                            ) : (
                                                                <span className="text-slate-400">—</span>
                                                            )}
                                                        </td>
                                                        <td className="px-2 py-2.5 text-center">
                                                            <button
                                                                type="button"
                                                                onClick={(e) => openContactsForCompany(e, c.id)}
                                                                className="min-w-[2rem] rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-black text-[#e51b23] hover:bg-red-50 hover:border-red-200 transition-colors tabular-nums"
                                                                title="Ga naar contactpersonen (dit bedrijf)"
                                                            >
                                                                {st.contacts}
                                                            </button>
                                                        </td>
                                                        <td className="px-2 py-2.5 text-center">
                                                            <button
                                                                type="button"
                                                                onClick={(e) => openShortlistForCompany(e, c.id)}
                                                                className="min-w-[2rem] rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-black text-indigo-700 hover:bg-indigo-50 hover:border-indigo-200 transition-colors tabular-nums"
                                                                title="Ga naar shortlist (vacatures van dit bedrijf)"
                                                            >
                                                                {st.shortlist}
                                                            </button>
                                                        </td>
                                                        <td className="px-2 py-2.5 text-center">
                                                            <button
                                                                type="button"
                                                                onClick={(e) => openVacanciesForCompany(e, c.name || '')}
                                                                className="min-w-[2rem] rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-black text-slate-800 hover:bg-slate-100 transition-colors tabular-nums"
                                                                title="Ga naar vacatures (filter op dit bedrijf)"
                                                            >
                                                                {st.vacancies}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    );
                                                })
                                            )
                                        ) : (
                                            filteredContacts.length === 0 ? (
                                                <tr><td colSpan={5} className="py-20 text-center"><p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Geen contactpersonen gevonden.</p></td></tr>
                                            ) : (
                                                filteredContacts.map((c) => (
                                                    <tr key={c.id} onClick={() => handleEdit(c)} className="cursor-pointer transition-all hover:bg-slate-50 group border-l-4 border-l-transparent hover:border-l-red-300">
                                                        <td className="px-4 py-3 w-16 align-top">
                                                            <div className="text-[10px] font-black text-slate-400 font-mono select-none mt-2">{c.bullhorn_id ? `#${c.bullhorn_id}` : '-'}</div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="flex items-start gap-3">
                                                                <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs border overflow-hidden shrink-0 bg-blue-50 border-blue-100 text-blue-600">
                                                                    {(c.first_name?.[0] || '')}{(c.last_name?.[0] || '')}
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <div className="flex items-center gap-2 mb-0.5">
                                                                        <h4 className="text-xs font-black leading-tight truncate text-slate-900 group-hover:text-[#e51b23] transition-colors">{c.first_name} {c.last_name}</h4>
                                                                        <div className="flex gap-1">
                                                                            {c.active_clientportal === false ? (
                                                                                <span title="Persoonlijk geblokkeerd" className="bg-red-50 text-red-600 px-1.5 py-0.5 rounded-[4px] text-[8px] font-black uppercase tracking-widest border border-red-200 inline-flex items-center">Geblokkeerd</span>
                                                                            ) : (c.linked_org?.active_clientportal || clientPortalInvites.includes(c.email?.toLowerCase())) ? (
                                                                                <span title={c.linked_org?.active_clientportal ? "Toegang via bedrijf" : "Individuele invite"} className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-[4px] text-[8px] font-black uppercase tracking-widest border border-blue-200 shadow-sm flex items-center gap-1"><ExternalLink size={8}/> Portaal Toegang</span>
                                                                            ) : null}
                                                                        </div>
                                                                    </div>
                                                                    <p className="text-[10px] font-medium truncate max-w-[200px] text-slate-500 flex items-center gap-1.5"><Mail size={10} className="text-slate-400"/> {c.email || 'Geen email'}</p>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 hidden md:table-cell">
                                                            <div className="flex flex-col gap-1">
                                                                <span className="text-[11px] font-black text-slate-700 flex items-center gap-1.5">
                                                                    <Building2 size={12} className="text-slate-400" /> {c.linked_org?.name || 'Onbekend Bedrijf'}
                                                                </span>
                                                                {c.job_title && <span className="text-[10px] font-bold text-slate-500 flex items-center gap-1.5">{c.job_title}</span>}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-center hidden md:table-cell">
                                                            {c.bullhorn_id ? (
                                                                <span className="inline-flex items-center gap-1 bg-orange-50 text-orange-600 px-2 py-0.5 rounded text-[9px] font-black tracking-widest uppercase border border-orange-100"><RefreshCw size={8}/> Bullhorn</span>
                                                            ) : (
                                                                <span className="inline-flex bg-slate-100 text-slate-500 px-2 py-0.5 rounded text-[9px] font-black tracking-widest uppercase border border-slate-200">Lokaal</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-right">
                                                            <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <ChevronRight size={14} className="text-slate-400 group-hover:text-red-500 ml-1" />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    )}
                    
                    {/* Entity Dashboard Inline */}
                    {showDashboard && (
                        <CRMDashboard
                            entity={editingEntity}
                            entityType={activeTab === 'companies' ? 'company' : 'contact'}
                            onBack={() => setShowDashboard(false)}
                            onSaveSuccess={() => {
                                setShowDashboard(false);
                                loadData();
                            }}
                            user={user}
                            onNavigateVacancy={onNavigateVacancy}
                            onNavigateCandidate={onNavigateCandidate}
                            onLoginAs={onLoginAs}
                            onNavigateToCompanies={() => {
                                setShowDashboard(false);
                                setActiveTab('companies');
                            }}
                            onNavigateToShortlist={() => {
                                setShowDashboard(false);
                                onNavigateToShortlist?.();
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default CRMManager;
